import {ButtonColor} from '@ui/buttons/get-shared-button-style';
import {useSettings} from '@ui/settings/use-settings';
import {useNavigate} from '@common/ui/navigation/use-navigate';
import {Menu, MenuTrigger} from '@ui/menu/menu-trigger';
import {IconButton} from '@ui/buttons/icon-button';
import {PersonIcon} from '@ui/icons/material/Person';
import {Item} from '@ui/forms/listbox/item';
import {Trans} from '@ui/i18n/trans';
import {Link} from 'react-router-dom';
import {Button} from '@ui/buttons/button';
import {NavbarProps} from '@common/ui/navigation/navbar/navbar';
import {Fragment} from 'react';
import {useSocialLogin} from '@common/auth/requests/use-social-login';
import {useAuth} from '@common/auth/use-auth';
import {toast} from '@ui/toast/toast';
import {useTrans} from '@ui/i18n/use-trans';
import {message} from '@ui/i18n/message';

/**
 * Когда включён вход через MyIT, кнопка «Войти» открывает окно провайдера
 * сразу, минуя страницу с формой логина и пароля: на этом сайте MyIT —
 * основной способ входа, и лишний переход только мешает.
 *
 * Страница `/login` при этом остаётся доступной по прямому адресу —
 * это страховка на случай, если MyIT недоступен.
 */
function useDirectMyitLogin() {
  const {social} = useSettings();
  const {loginWithSocial} = useSocialLogin();
  const {getRedirectUri} = useAuth();
  const navigate = useNavigate();
  const {trans} = useTrans();

  if (!social?.myit?.enable) {
    return null;
  }

  return async () => {
    const e = await loginWithSocial('secure/auth/myit/login');

    if (e?.status === 'SUCCESS' || e?.status === 'ALREADY_LOGGED_IN') {
      navigate(getRedirectUri(), {replace: true});
      return;
    }

    // Аккаунт MyIT с таким же email уже есть локально — вендорский сценарий
    // просит подтвердить пароль. Диалог для этого живёт на странице входа,
    // поэтому уводим туда, а не молчим.
    if (e?.status === 'REQUEST_PASSWORD') {
      toast(
        trans(
          message('Confirm your password to link the accounts.'),
        ),
      );
      navigate('/login');
    }
  };
}

interface NavbarAuthButtonsProps {
  primaryButtonColor?: ButtonColor;
  navbarColor?: NavbarProps['color'];
}
export function NavbarAuthButtons({
  primaryButtonColor,
  navbarColor,
}: NavbarAuthButtonsProps) {
  if (!primaryButtonColor) {
    primaryButtonColor = navbarColor === 'primary' ? 'paper' : 'primary';
  }

  return (
    <Fragment>
      <MobileButtons />
      <DesktopButtons primaryButtonColor={primaryButtonColor} />
    </Fragment>
  );
}

interface DesktopButtonsProps {
  primaryButtonColor: ButtonColor;
}
function DesktopButtons({primaryButtonColor}: DesktopButtonsProps) {
  const {registration} = useSettings();
  const loginWithMyit = useDirectMyitLogin();
  return (
    <div className="text-sm max-md:hidden">
      {!registration.disable && (
        <Button
          elementType={Link}
          to="/register"
          variant="text"
          className="mr-10"
        >
          <Trans message="Register" />
        </Button>
      )}
      {loginWithMyit ? (
        <Button
          variant="raised"
          color={primaryButtonColor}
          onClick={loginWithMyit}
        >
          <Trans message="Login" />
        </Button>
      ) : (
        <Button
          elementType={Link}
          to="/login"
          variant="raised"
          color={primaryButtonColor}
        >
          <Trans message="Login" />
        </Button>
      )}
    </div>
  );
}

function MobileButtons() {
  const {registration} = useSettings();
  const navigate = useNavigate();
  const loginWithMyit = useDirectMyitLogin();
  return (
    <MenuTrigger>
      <IconButton size="md" className="md:hidden">
        <PersonIcon />
      </IconButton>
      <Menu>
        <Item
          value="login"
          onSelected={() => (loginWithMyit ? loginWithMyit() : navigate('/login'))}
        >
          <Trans message="Login" />
        </Item>
        {!registration.disable && (
          <Item value="register" onSelected={() => navigate('/register')}>
            <Trans message="Register" />
          </Item>
        )}
      </Menu>
    </MenuTrigger>
  );
}
